import type {
  Building, CorpseMark, DeathRecord, Deposit, Mine, PendingSummon, Projectile, RitualState,
  Ruin, SimEvent, SiteState, Squad, WorkerEnt, Zone, MatchState, PlayerState,
} from '../sim/state';
import type { Sim } from '../sim/sim';
import type { PlayerId } from '../content/types';

type CollectionName = 'squads' | 'workers' | 'buildings' | 'deposits' | 'projectiles' | 'mines' | 'zones';

type ViewOmissions = 'rng' | CollectionName | 'connectedConduits' | 'nextId' | 'nextFieldId' | 'recentDeaths';

export type MatchViewSnapshot = Omit<MatchState, ViewOmissions> & {
  viewPlayer: PlayerId;
  squads: Squad[];
  workers: WorkerEnt[];
  buildings: Building[];
  deposits: Deposit[];
  projectiles: Projectile[];
  mines: Mine[];
  zones: Zone[];
  connectedConduits: number[];
};

export interface EntityDelta {
  squads?: Squad[];
  workers?: WorkerEnt[];
  buildings?: Building[];
  deposits?: Deposit[];
  projectiles?: Projectile[];
  mines?: Mine[];
  zones?: Zone[];
}

export interface EntityRemoval {
  squads?: number[];
  workers?: number[];
  buildings?: number[];
  deposits?: number[];
  projectiles?: number[];
  mines?: number[];
  zones?: number[];
}

export interface StateDelta {
  baseSnapshotSeq: number;
  tick: number;
  time: number;
  phase: MatchState['phase'];
  countdownRemaining: number;
  winningTeam: MatchState['winningTeam'];
  paused: boolean;
  players?: PlayerState[];
  entities?: EntityDelta;
  removed?: EntityRemoval;
  ruins?: Ruin[];
  sites?: SiteState[];
  corpseMarks?: CorpseMark[];
  rituals?: RitualState[];
  pendingSummons?: PendingSummon[];
  connectedConduits?: number[];
  events: SimEvent[];
}

export type MatchViewDelta = StateDelta;

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value, (_key, child) => (child === Infinity ? null : child))) as T;
}

function hydratePlayers(players: PlayerState[]): PlayerState[] {
  return players.map((player) => ({
    ...player,
    cards: Object.fromEntries(
      Object.entries(player.cards).map(([id, card]) => [id, { ...card, usesRemaining: card.usesRemaining === null ? Infinity : card.usesRemaining }]),
    ),
  }));
}

function projectPlayers(players: PlayerState[], viewer: PlayerId): PlayerState[] {
  const projected = cloneJson(players);
  for (const player of projected) {
    if (player.id === viewer) continue;
    Object.assign(player, {
      power: 0,
      essence: 0,
      allocationPower: 0,
      autoWorker: false,
      cards: {},
      deck: [],
      shelterOccupants: [],
      workerQueue: [],
      noEssenceWarning: false,
      corpseStored: 0,
      awakeningRemaining: 0,
      devouredMass: 0,
      convergenceRemaining: 0,
    });
  }
  return projected;
}

function projectSquad(squad: Squad, viewer: PlayerId): Squad {
  if (squad.owner === viewer) return squad;
  return {
    ...squad,
    command: { kind: 'idle', pointA: { ...squad.pos } },
    path: null,
    pathIndex: 0,
    pathGoal: null,
    repathCooldown: 0,
    stuckTime: 0,
    moveTarget: null,
    targetId: null,
    retargetTimer: 0,
    // Formation pace is order intent, so it is hidden from opponents like the rest of it. The
    // client interpolates enemy squads from snapshots rather than simulating them, so nothing
    // downstream reads this for a non-viewer.
    groupSpeedCap: null,
    windupRemaining: 0,
    windupTargetId: null,
    abilityCooldowns: {},
    abilityFiredOnce: {},
  };
}

function projectWorker(worker: WorkerEnt, viewer: PlayerId): WorkerEnt {
  if (worker.owner === viewer) return worker;
  return {
    ...worker,
    homeId: null,
    carrying: null,
    depositId: null,
    buildTargetId: null,
    repairTargetId: null,
    path: null,
    pathIndex: 0,
  };
}

function projectBuilding(building: Building, viewer: PlayerId): Building {
  if (building.owner === viewer) return building;
  return {
    ...building,
    queue: [],
    autoProduce: {},
    rally: { ...building.pos },
    upgradeRemaining: building.upgradeRemaining > 0 ? 1 : 0,
    upgradeInProgress: building.upgradeInProgress ? { id: 'researching', remaining: 1 } : null,
    workerAuto: false,
    workerQueueRemaining: building.workerQueueRemaining === null ? null : 1,
    recallActive: false, // enemy recall state is their business
  };
}

export function serializeMatchView(state: MatchState, viewPlayer: PlayerId): MatchViewSnapshot {
  return cloneJson({
    viewPlayer,
    tick: state.tick,
    time: state.time,
    phase: state.phase,
    countdownRemaining: state.countdownRemaining,
    winningTeam: state.winningTeam,
    seed: state.seed,
    players: projectPlayers(state.players, viewPlayer),
    squads: [...state.squads.values()].map((squad) => projectSquad(squad, viewPlayer)),
    workers: [...state.workers.values()].map((worker) => projectWorker(worker, viewPlayer)),
    buildings: [...state.buildings.values()].map((building) => projectBuilding(building, viewPlayer)),
    deposits: [...state.deposits.values()],
    projectiles: [...state.projectiles.values()],
    mines: [...state.mines.values()],
    zones: [...state.zones.values()],
    ruins: state.ruins,
    sites: state.sites,
    corpseMarks: state.corpseMarks,
    rituals: state.rituals,
    pendingSummons: state.pendingSummons,
    connectedConduits: [...state.connectedConduits],
    events: state.events,
    paused: state.paused,
  });
}

export function hashMatchState(state: MatchState): string {
  const canonical = JSON.stringify(cloneJson({
    tick: state.tick,
    time: state.time,
    phase: state.phase,
    winningTeam: state.winningTeam,
    players: state.players,
    squads: [...state.squads.values()],
    workers: [...state.workers.values()],
    buildings: [...state.buildings.values()],
    deposits: [...state.deposits.values()],
    projectiles: [...state.projectiles.values()],
    mines: [...state.mines.values()],
    zones: [...state.zones.values()],
    ruins: state.ruins,
    sites: state.sites,
    corpseMarks: state.corpseMarks,
    rituals: state.rituals,
    pendingSummons: state.pendingSummons,
    connectedConduits: [...state.connectedConduits],
  }));
  let hash = 0x811c9dc5;
  for (let i = 0; i < canonical.length; i++) {
    hash ^= canonical.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export function applyMatchView(sim: Sim, snapshot: MatchViewSnapshot): void {
  const previousRng = sim.state.rng;
  sim.state = {
    ...sim.state,
    ...snapshot,
    rng: previousRng,
    players: hydratePlayers(snapshot.players),
    squads: new Map(snapshot.squads.map((entity) => [entity.id, entity])),
    workers: new Map(snapshot.workers.map((entity) => [entity.id, entity])),
    buildings: new Map(snapshot.buildings.map((entity) => [entity.id, entity])),
    deposits: new Map(snapshot.deposits.map((entity) => [entity.id, entity])),
    projectiles: new Map(snapshot.projectiles.map((entity) => [entity.id, entity])),
    mines: new Map(snapshot.mines.map((entity) => [entity.id, entity])),
    zones: new Map(snapshot.zones.map((entity) => [entity.id, entity])),
    connectedConduits: new Set(snapshot.connectedConduits),
  };
}

function changed<T>(a: T, b: T): boolean {
  return JSON.stringify(a) !== JSON.stringify(b);
}

function diffEntities<T extends { id: number }>(before: T[], after: T[]): { upserts: T[]; removed: number[] } {
  const oldById = new Map(before.map((entity) => [entity.id, entity]));
  const newById = new Map(after.map((entity) => [entity.id, entity]));
  const upserts = after.filter((entity) => !oldById.has(entity.id) || changed(oldById.get(entity.id), entity));
  const removed = before.filter((entity) => !newById.has(entity.id)).map((entity) => entity.id);
  return { upserts, removed };
}

export function makeStateDelta(before: MatchViewSnapshot, after: MatchViewSnapshot, baseSnapshotSeq: number): MatchViewDelta {
  const delta: StateDelta = {
    baseSnapshotSeq,
    tick: after.tick,
    time: after.time,
    phase: after.phase,
    countdownRemaining: after.countdownRemaining,
    winningTeam: after.winningTeam,
    paused: after.paused,
    events: after.events,
    entities: {},
    removed: {},
  };

  for (const name of ['squads', 'workers', 'buildings', 'deposits', 'projectiles', 'mines', 'zones'] as const) {
    const diff = diffEntities(before[name] as Array<{ id: number }>, after[name] as Array<{ id: number }>);
    if (diff.upserts.length) Object.assign(delta.entities!, { [name]: diff.upserts });
    if (diff.removed.length) delta.removed![name] = diff.removed;
  }
  if (changed(before.players, after.players)) delta.players = after.players;
  if (changed(before.ruins, after.ruins)) delta.ruins = after.ruins;
  if (changed(before.sites, after.sites)) delta.sites = after.sites;
  if (changed(before.corpseMarks, after.corpseMarks)) delta.corpseMarks = after.corpseMarks;
  if (changed(before.rituals, after.rituals)) delta.rituals = after.rituals;
  if (changed(before.pendingSummons, after.pendingSummons)) delta.pendingSummons = after.pendingSummons;
  if (changed(before.connectedConduits, after.connectedConduits)) delta.connectedConduits = after.connectedConduits;
  return delta;
}

function applyEntityDelta<T extends { id: number }>(base: T[], upserts: T[] | undefined, removed: number[] | undefined): T[] {
  const byId = new Map(base.map((entity) => [entity.id, entity]));
  for (const id of removed ?? []) byId.delete(id);
  for (const entity of upserts ?? []) byId.set(entity.id, entity);
  return [...byId.values()].sort((a, b) => a.id - b.id);
}

export function applyStateDelta(base: MatchViewSnapshot, delta: MatchViewDelta): MatchViewSnapshot {
  const next = cloneJson(base);
  next.tick = delta.tick;
  next.time = delta.time;
  next.phase = delta.phase;
  next.countdownRemaining = delta.countdownRemaining;
  next.winningTeam = delta.winningTeam;
  next.paused = delta.paused;
  next.events = delta.events;
  for (const name of ['squads', 'workers', 'buildings', 'deposits', 'projectiles', 'mines', 'zones'] as const) {
    next[name] = applyEntityDelta(
      next[name] as Array<{ id: number }>,
      delta.entities?.[name] as Array<{ id: number }> | undefined,
      delta.removed?.[name],
    ) as never;
  }
  if (delta.players) next.players = delta.players;
  if (delta.ruins) next.ruins = delta.ruins;
  if (delta.sites) next.sites = delta.sites;
  if (delta.corpseMarks) next.corpseMarks = delta.corpseMarks;
  if (delta.rituals) next.rituals = delta.rituals;
  if (delta.pendingSummons) next.pendingSummons = delta.pendingSummons;
  if (delta.connectedConduits) next.connectedConduits = delta.connectedConduits;
  return next;
}
